import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva } from 'class-variance-authority';

import { cn } from './cn';
import { Label } from './Label';
import { Text } from './Typography';

// Binary on/off control. Tone drives the *checked* surface colour
// (neutral=secondary, info/success/warning/destructive map to the
// status palette). Optional label and description compose Label + Text.

const SWITCH_ROOT_BASE_CLASSNAME = 'tw-peer tw-inline-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-rounded-full tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-50';

const SWITCH_THUMB_CLASSNAME = 'tw-pointer-events-none tw-block tw-rounded-full tw-bg-background tw-shadow-none tw-ring-0 tw-transition-transform';

const switchVariants = cva(
    'tw-border tw-border-transparent tw-bg-muted focus:tw-ring-0 focus-visible:tw-ring-0 data-[state=unchecked]:tw-bg-muted [&>span]:tw-bg-background [&>span]:tw-shadow-none',
    {
        variants: {
            size: {
                small: 'tw-h-5 tw-w-9 [&>span]:tw-h-4 [&>span]:tw-w-4 data-[state=checked]:[&>span]:tw-translate-x-4',
                medium: 'tw-h-6 tw-w-11 [&>span]:tw-h-5 [&>span]:tw-w-5 data-[state=checked]:[&>span]:tw-translate-x-5'
            },
            tone: {
                neutral: 'data-[state=unchecked]:tw-border-input data-[state=unchecked]:tw-bg-muted data-[state=checked]:tw-border-transparent data-[state=checked]:tw-bg-secondary',
                info: 'data-[state=unchecked]:tw-border-input data-[state=unchecked]:tw-bg-muted data-[state=checked]:tw-border-transparent data-[state=checked]:tw-bg-info',
                success: 'data-[state=unchecked]:tw-border-input data-[state=unchecked]:tw-bg-muted data-[state=checked]:tw-border-transparent data-[state=checked]:tw-bg-success',
                warning: 'data-[state=unchecked]:tw-border-input data-[state=unchecked]:tw-bg-muted data-[state=checked]:tw-border-transparent data-[state=checked]:tw-bg-warning',
                destructive: 'data-[state=unchecked]:tw-border-input data-[state=unchecked]:tw-bg-muted data-[state=checked]:tw-border-transparent data-[state=checked]:tw-bg-destructive'
            }
        },
        defaultVariants: {
            size: 'medium',
            tone: 'neutral'
        }
    }
);

const Switch = React.forwardRef(({ className: _className, style: _style, description, disabled, id, label, size, tone, ...props }, ref) => {
    const generatedId = React.useId();
    const switchId = id ?? generatedId;

    return (
        <div className="tw-flex tw-items-center tw-gap-3">
            <SwitchPrimitives.Root
                ref={ref}
                id={switchId}
                disabled={disabled}
                className={cn(SWITCH_ROOT_BASE_CLASSNAME, switchVariants({ size, tone }))}
                {...props}
            >
                <SwitchPrimitives.Thumb className={SWITCH_THUMB_CLASSNAME} />
            </SwitchPrimitives.Root>

            {(label || description) && (
                <div className="tw-grid tw-gap-1 tw-leading-none">
                    {label ? (
                        <Label htmlFor={switchId} disabled={disabled}>
                            {label}
                        </Label>
                    ) : null}
                    {description ? (
                        <Text tone="muted" variant="body-sm">
                            {description}
                        </Text>
                    ) : null}
                </div>
            )}
        </div>
    );
});

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
